import React, { useMemo } from "react";
import { Button, Input, Select } from "antd";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";

import { useBatteries } from "misc/api/datalayer";

const BatterySelector = ({ defaultValue, value, onChange, multiselect, browseWithArrows = false, ...props }) => {
    const [t] = useTranslation();
    const { batteries = [] } = useBatteries({ suspense: true });

    const batteryIds = useMemo(() => batteries.map(({ batteryId }) => batteryId), [batteries]);
    const selectedIndex = useMemo(() => batteryIds.indexOf(value), [batteryIds, value]);

    const [disabledPrevious, disabledNext] = useMemo(() => {
        if (!batteries.length || !value || selectedIndex === -1) return [true, true];

        return [selectedIndex <= 0, selectedIndex >= batteries.length - 1];
    }, [batteries.length, selectedIndex, value]);

    const [handlePrevious, handleNext] = useMemo(() => {
        if (multiselect || !value || !batteries.length || selectedIndex === -1) return [undefined, undefined];

        const previousBatteryId = batteries[!disabledPrevious ? selectedIndex - 1 : selectedIndex].batteryId;
        const nextBatteryId = batteries[!disabledNext ? selectedIndex + 1 : selectedIndex].batteryId;

        return [() => onChange(previousBatteryId), () => onChange(nextBatteryId)];
    }, [batteries, disabledNext, disabledPrevious, multiselect, onChange, selectedIndex, value]);

    const showBrowseArrows = useMemo(() => browseWithArrows && !multiselect, [browseWithArrows, multiselect]);

    return (
        <Input.Group compact>
            {showBrowseArrows && (
                <Button onClick={handlePrevious} disabled={disabledPrevious} icon={<FontAwesomeIcon icon={["fal", "chevron-left"]} />} />
            )}

            <Select
                defaultValue={defaultValue}
                value={value}
                onChange={onChange}
                mode={multiselect ? "multiple" : null}
                optionFilterProp="label"
                optionLabelProp="value"
                placeholder={t(multiselect ? "t_select_batteries" : "t_select_battery")}
                style={{ width: "100%" }}
                maxTagCount="responsive"
                showSearch
                {...props}
            >
                {batteries.map(battery => (
                    <Select.Option key={battery.batteryId} value={battery.batteryId} label={battery.batteryId}>
                        {battery.batteryId}
                    </Select.Option>
                ))}
            </Select>

            {showBrowseArrows && <Button onClick={handleNext} disabled={disabledNext} icon={<FontAwesomeIcon icon={["fal", "chevron-right"]} />} />}
        </Input.Group>
    );
};

BatterySelector.propTypes = {
    defaultValue: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    onChange: PropTypes.func,
    multiselect: PropTypes.bool,
    browseWithArrows: PropTypes.bool
};

export default BatterySelector;
